import { useQuery, useMutation, useLazyQuery } from '@apollo/react-hooks';
import { Edit, Settings } from '@jda/lui-common-icon-library';
import History from '@jda/lui-common-icon-library/ui/History';
import { Button } from '@material-ui/core';
import * as _ from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import DetailedTable from '../../components/DetailedTable';
import AuditDetailsDialog from '../../components/AuditHistory';
import { Loading } from '../../Loading';
import {
  SUBSCRIBED_PARTICIPANT_WORKFLOW_QUERY,
  WORKFLOW_LIST,
  WORKFLOW_SUBSCRIBERS_SUMMARY_QUERY,
  FETCH_AUTHENTICATION_QUERY,
  ENABLE_SERVICE,
  USER_DETAILS,
  FETCH_SERVICE_AUDIT_HISTORY_QUERY
} from '../graphQL/queries';
import columns from './columns';
import EnableServiceDialog from './EnableServiceDialog';
import ServiceSettingsDialog from './ServiceSettingsDialog';
import AggregatorSettingsDialog from './AggregatorSettingsDialog';
import EnableServiceFormDataConfig from './formDataConfig';
import ViewDetails from '@jda/lui-common-icon-library/ui/ViewDetails';

type workflowInfo = {
  workflowCode: string;
  workflowName: string;
  operations?: any;
};

type workflowObj = {
  workflow: workflowContentData;
};
type workflowContentData = {
  content: workflowInfo[];
};

interface ParticipantServiceProps {
  participant: any;
  participantCode: any;
}

type initDataType = {
  enableService: false | true;
  formData: any;
  isEdit: false | true;
  editSettings: false | true;
  aggregatorEdit: false | true;
};

const OTHER_SERVICES_OPERATIONS_URL: any = {
  quoteRequest: '/api/v1/dynamicCapacity/quoteRequest',
  quoteResponse: '/api/v1/dynamicCapacity/quoteResponse',
  tenderNotification: '/api/v1/dynamicCapacity/tenderNotification',
  tenderResponse: '/api/v1/dynamicCapacity/tenderResponse',
  loadNotification: '/api/v1/transportationMarketRates/loadNotification',
  rateRequest: '/api/v1/transportationMarketRates/rateRequest',
  recentTransactions: '/api/v1/transportationMarketRates/recentTransactions',
  event: '/api/1/rest/feed-master/queue/TysonNonProd/Ultra_PoC/10-QA-team-odyssey-bytms/tk_tms_sl_event_queue_producer'
};

const OPERATIONS_URL: any = {
  booking_cancelResponse: '/api/v1/carrierCollaboration/booking/cancelResponse',
  booking_createResponse: '/api/v1/carrierCollaboration/booking/createResponse',
  booking_updateResponse: '/api/v1/carrierCollaboration/booking/updateResponse',
  documentResponse: '/api/v1/carrierCollaboration/documentResponse',
  informationResponse: '/api/v1/carrierCollaboration/informationResponse',
  ltl_rateShipmentResponse: '/api/v1/carrierCollaboration/ltl/rateShipmentResponse',
  parcel_closeManifestResponse: '/api/v1/carrierCollaboration/parcel/closeManifestResponse',
  parcel_createManifestResponse: '/api/v1/carrierCollaboration/parcel/createManifestResponse',
  parcel_rateShipmentResponse: '/api/v1/carrierCollaboration/parcel/rateShipmentResponse',
  tenderResponse: '/api/v1/carrierCollaboration/tenderResponse',
  ocean_bookingResponse: '/api/v1/carrierCollaboration/ocean/bookingResponse',
  booking_cancel: '/api/v1/carrierCollaboration/booking/cancel',
  booking_create: '/api/v1/carrierCollaboration/booking/create',
  booking_update: '/api/v1/carrierCollaboration/booking/update',
  documentRequest: '/api/v1/carrierCollaboration/documentRequest',
  informationRequest: '/api/v1/carrierCollaboration/informationRequest',
  ltl_rateShipmentRequest: '/api/v1/carrierCollaboration/ltl/rateShipmentRequest',
  ocean_bookingNotification: '/api/v1/carrierCollaboration/ocean/bookingNotification',
  parcel_closeManifest: '/api/v1/carrierCollaboration/parcel/closeManifest',
  parcel_createManifest: '/api/v1/carrierCollaboration/parcel/createManifest',
  parcel_rateShipmentRequest: '/api/v1/carrierCollaboration/parcel/rateShipmentRequest',
  tenderNotification: '/api/v1/carrierCollaboration/tenderNotification'
};

const ParticipantService = (props: ParticipantServiceProps) => {
  let { participantType } = props.participant;
  let { participantCode } = props;
  const [formInfo, setFormInfo] = React.useState<any>({});
  const [rowData, setRowData] = React.useState<any>({});
  const [domain, setDomain] = React.useState<string>('');
  const [serviceAuthContext, setServiceAuthContext] = React.useState<any>('');
  const [serviceAuthentication, isServiceAuthentication] = React.useState('no');
  const [requireTransform, setRequireTransform] = React.useState('no');
  const [providerCustomerCodePath, setProviderCustomerCodePath] = React.useState<any>('');
  const [defaultAuthContext, setDefaultAuthContext] = React.useState<any>('');
  const [authContextList, setAuthContextList] = React.useState<any>([]);
  const [auditHistory, setAuditHistory] = React.useState(false);
  const [formData, setFormData] = React.useState<any>({});
  const [enableServiceQuery] = useMutation(ENABLE_SERVICE);
  const { enqueueSnackbar } = useSnackbar();
  const { loading, data, refetch } = useQuery(WORKFLOW_SUBSCRIBERS_SUMMARY_QUERY, {
    variables: {
      participantType: participantType,
      participantCode: participantCode
    },
    skip: !participantType || !participantCode
  });
  const { data: userData } = useQuery(USER_DETAILS, { fetchPolicy: 'cache-only' });
  const workflowList = useQuery<workflowObj, {}>(WORKFLOW_LIST, {});
  const [userRole, setUserRole] = React.useState('');
  const allAuthentications = useQuery(FETCH_AUTHENTICATION_QUERY, {
    variables: { participantCode: participantCode }
  });

  const [
    fetchServiceAuditHistoryData,
    { loading: auditLoading, data: auditData, refetch: auditRefetch }
  ] = useLazyQuery(FETCH_SERVICE_AUDIT_HISTORY_QUERY, {
    fetchPolicy: 'network-only'
  });

  const [initData, setInitData] = React.useState<initDataType>({
    enableService: false,
    formData: {},
    isEdit: false,
    editSettings: false,
    aggregatorEdit: false
  });

  const getCount = (service: string, list: any) => {
    const res = _.filter(list || [], (obj: any) => obj.service === service);
    return res?.[0]?.count || 0;
  };

  useEffect(() => {
    setUserRole(userData?.userProfile?.roles?.[0]);
  }, [userData]);

  useEffect(() => {
    if (props.participant?.errorDayCountData?.participantErrorCounts) {
      (data?.workflowSubscriberSummary || []).forEach((obj: any) => {
        obj.todayErrorCount = getCount(obj.workflowCode, props.participant?.errorDayCountData?.participantErrorCounts);
      });
    }
    if (props.participant?.errorHourCountData?.participantErrorCounts) {
      (data?.workflowSubscriberSummary || []).forEach((obj: any) => {
        obj.lastHourErrorCount = getCount(
          obj.workflowCode,
          props.participant?.errorHourCountData?.participantErrorCounts
        );
      });
    }
  }, [data, props.participant]);

  const participantServiceActions: any = [
    userRole === 'APP_ADMIN' && {
      icon: () => (
        <Button
          variant="outlined"
          disabled={isEnableDisabled()}
          onClick={handleEnableServiceDialog}
          data-id="enable-service"
          color="primary"
        >
          Enable Service
        </Button>
      ),
      isFreeAction: true,
      onClick: () => {}
      // position: isActionOnSelect ? 'toolbarOnSelect' : 'toolbar'
    },
    {
      icon: () => <History />,
      onClick: (event: any, rowData: any) => handleAuditHistory(rowData),
      tooltip: 'View History',
      position: 'row'
    },
    userRole === 'APP_ADMIN' || userRole === 'PARTICIPANT_ADMIN'
      ? {
          icon: () => <Edit data-id="edit-enable-service" />,
          onClick: (event: any, rowData: any) => editHandleEnableServiceDialog(rowData)
        }
      : {
          icon: () => <ViewDetails />,
          onClick: (event: any, rowData: any) => editHandleEnableServiceDialog(rowData),
          tooltip: 'View Details',
          position: 'row'
        },
    (userRole === 'APP_ADMIN' || userRole === 'APP_SUPPORT') && {
      icon: () => <Settings data-id="servie-settings" />,
      onClick: (event: any, rowData: any) => toggleSettingsDialog(rowData),
      tooltip: 'Retry Details'
    },
    (userRole === 'APP_ADMIN' || userRole === 'APP_SUPPORT') && {
      icon: () => <ViewDetails data-id="servie-aggregator-settings" />,
      onClick: (event: any, rowData: any) => aggregatorSettingsDialog(rowData),
      tooltip: 'Aggregator Details'
    }
  ];

  const handleAuditHistory = (rowData: any) => {
    fetchServiceAuditHistoryData({
      variables: {
        participantCode: participantCode,
        workflowCode: rowData.workflowCode
      }
    });
    setFormData({ ...rowData });
    setAuditHistory(true);
  };

  const handleAuditHistoryClose = () => {
    setFormData({});
    setAuditHistory(false);
  };

  const handleEnableServiceDialogClose = () => {
    setInitData({
      ...initData,
      enableService: false,
      editSettings: false,
      aggregatorEdit: false
    });
    setRequireTransform('no');
    setProviderCustomerCodePath('');
    isServiceAuthentication('no');
    setServiceAuthContext(defaultAuthContext);
    setDomain('');
    setFormInfo({
      ...formInfo,
      operations: {},
      workflowCode: '',
      tid: '',
      selectAll: ''
    });
  };

  const isEnableDisabled = () => {
    if (getListOfServices()?.length > 0) return false;
    return true;
  };

  const getListOfServices = () => {
    return (workflowList.data?.workflow?.content || []).filter(
      workflow =>
        !(subscribedParticipantData?.subscribedParticipantWorkflow?.content || []).some(
          (subscribedWorkflow: { workflowCode: string }) => subscribedWorkflow.workflowCode === workflow.workflowCode
        )
    );
  };

  const handleEnableServiceDialog = () => {
    let servicesList = [] as any;
    _.forEach(getListOfServices() || [], (element: any) => {
      const obj = {
        label: element.workflowName,
        value: element.workflowCode
      };
      servicesList.push(obj);
    });
    const serviceNameFormObj = _.filter(
      EnableServiceFormDataConfig[participantType] || [],
      (o: any) => o.key === 'workflowCode'
    );
    if (serviceNameFormObj) {
      serviceNameFormObj[0].values = servicesList;
      serviceNameFormObj[0].disabled = false;
    }
    setInitData({
      ...initData,
      enableService: true,
      isEdit: false,
      formData: {}
    });
  };

  const {
    loading: subscribedParticipantLoading,
    data: subscribedParticipantData,
    refetch: subscribedParticipantRefresh
  } = useQuery(SUBSCRIBED_PARTICIPANT_WORKFLOW_QUERY, {
    variables: { participantCode: participantCode },
    fetchPolicy: 'cache-first'
  });

  const editHandleEnableServiceDialog = (row: any) => {
    if (participantType === 'PROVIDER') {
      row.requireTransform = row.requireTransform === true || row.requireTransform === 'yes' ? 'yes' : 'no';
    }

    const rowData = setFormInfoData(row);
    setInitData({
      ...initData,
      isEdit: true,
      enableService: true,
      formData: { ...rowData }
    });
  };

  const toggleSettingsDialog = (rowData: any) => {
    setRowData(rowData);
    const row = setFormInfoData(rowData);
    setInitData({
      ...initData,
      editSettings: !initData.editSettings,
      formData: { ...row }
    });
  };

  const aggregatorSettingsDialog = (rowData: any) => {
    setRowData(rowData);
    const row = setFormInfoData(rowData);
    setInitData({
      ...initData,
      aggregatorEdit: !initData.aggregatorEdit,
      formData: { ...row }
    });
  };

  const setFormInfoData = (row: any) => {
    let operations: any = {};
    _.forEach(subscribedParticipantData?.subscribedParticipantWorkflow?.content || [], (element: any) => {
      if (element.workflowCode === row.workflowCode) {
        row.interestedParties = element.interestedParties;
        row.authContext = element.authContext;
        row.aggregatorCode = element.aggregatorCode;
        row.tid = element.tid;
        row.connectionNote = element.connectionNote;
        _.forEach(element.enableOperations || [], async (operationElement: any) => {
          let retryFrequency = operationElement.retryFrequency;
          let maxRetries = operationElement.maxRetries;
          const autoRetry = (operationElement.maxRetries > 0 && operationElement.maxRetries !== null) || false;
          const useAuthContext = operationElement.authContext === 'DEFAULT' ? true : false;
          const useAggregator = (operationElement.aggregatorCode && operationElement.aggregatorCode !== null) || false;
          const aggregatorCode = operationElement.aggregatorCode;
          const authContext = operationElement.authContext;

          operations[operationElement.operationCode] = {
            url: operationElement.url,
            noOps: operationElement.noOps || false,
            retryFrequency,
            maxRetries,
            autoRetry,
            useAggregator,
            aggregatorCode,
            authContext,
            useAuthContext
          };
        });
      }
    });
    row.operations = operations;

    const obj = [
      {
        label: row.workflowName,
        value: row.workflowCode
      }
    ];

    const serviceNameFormObj = _.filter(
      EnableServiceFormDataConfig[participantType] || [],
      (o: any) => o.key === 'workflowCode'
    );
    if (serviceNameFormObj) {
      serviceNameFormObj[0].values = obj;
      serviceNameFormObj[0].disabled = true;
    }
    return row;
  };

  const handleOperationDataChange = (e: any, operation: any, field: string) => {
    const obj = formInfo.operations[operation];
    if (field === 'selectAll') {
      const objField = 'aggregatorCode';
      Object.keys(formInfo?.operations || {}).map((operationCode: any) => {
        const objAll = formInfo?.operations[operationCode];
        objAll[objField] = e.target.value;
      });
    } else {
      obj[field] =
        field !== 'noOps' && field !== 'autoRetry' && field !== 'useAggregator' && field !== 'useAuthContext'
          ? e.target.value
          : e.target.checked;

      if (field === 'useAggregator' || field === 'useAuthContext') {
        obj['authContext'] = null;
      }
    }
    setFormInfo({
      ...formInfo,
      operations: {
        ...formInfo.operations
      }
    });
  };

  const getParticipantType = (initiator: string) => {
    if ('CUSTOMER' === initiator) {
      return ['PROVIDER', 'AGGREGATOR'];
    } else if ('PROVIDER' === initiator) {
      return ['CUSTOMER', 'CUSTOMER_AGGREGATOR'];
    }
  };

  const getLatestValues = (data: any) => {
    setInitData({
      ...initData,
      ...{
        formData: {
          workflowCode: data.workflowCode
        }
      }
    });
    if (!initData.isEdit) {
      const operations: any = {};
      const serviceNameFormObj = _.filter(
        workflowList.data?.workflow?.content || [],
        (o: any) => o.workflowCode === data.workflowCode
      );
      (serviceNameFormObj?.[0]?.operations || []).forEach((operation: any) => {
        if (getParticipantType(operation?.initiator)?.indexOf(participantType) !== -1) {
          operations[operation.operationCode] = {
            url:
              (props.participant.participantType === 'CUSTOMER' &&
                (data.workflowCode === 'carrierCollaboration'
                  ? OPERATIONS_URL[operation.operationCode]
                  : OTHER_SERVICES_OPERATIONS_URL[operation.operationCode])) ||
              '',
            operationName: operation.operationName,
            retryFrequency: operation.retryFrequency || 0,
            maxRetries: operation.maxRetries || 0,
            autoRetry: false,
            useAggregator: false
          };
        }
      });
      data.operations = operations;
    }
    const info = { ...formInfo };

    setFormInfo({
      ...info,
      ...data
    });
  };

  useEffect(() => {
    const allAuth = setAuthContext(allAuthentications?.data?.fetchAuthentication?.content || []);
    setAuthContextList(allAuth);
  }, [allAuthentications]);

  const setAuthContext = (authList: any) => {
    let authContextList = [] as any;
    _.forEach(authList, (element: any) => {
      if (element.defaultAuth && serviceAuthentication === 'yes') {
        setServiceAuthContext(initData.formData.authContext || element.authContext);
        setDefaultAuthContext(element.authContext);
      }
      const obj = {
        label: element.authContext,
        value: element.authContext
      };
      authContextList.push(obj);
    });
    return authContextList;
  };

  const handleEnableServiceDialogSubmit = (formData: any) => {
    handleEnableServiceDialogClose();
    const operationsList: {
      domain: any;
      url: any;
      operationCode: string;
      workflowCode: any;
      participantCode: any;
    }[] = [];
    _.keys(formInfo.operations || []).forEach(key => {
      const obj = {
        domain: domain,
        url:
          props.participant.participantType === 'CUSTOMER'
            ? `${domain}${formInfo.operations[key]?.url}`
            : formInfo.operations[key]?.url,
        operationCode: key,
        workflowCode: formInfo.workflowCode,
        participantCode: participantCode,
        noOps: formInfo.operations[key]?.noOps || false,
        retryFrequency: formInfo.operations[key]?.autoRetry ? formInfo.operations[key]?.retryFrequency : null,
        maxRetries: formInfo.operations[key]?.autoRetry ? formInfo.operations[key]?.maxRetries : null,
        aggregatorCode: formInfo.operations[key]?.aggregatorCode,
        aggregatorCodeSelectAll: '',
        selectAll: false,
        authContext:
          // formInfo.operations[key]?.useAggregator || formInfo.operations[key]?.useAuthContext
          // ?
          formInfo.operations[key]?.authContext || null
        // : null
      };
      operationsList.push(obj);
    });

    enableServiceQuery({
      variables: {
        workflowCode: formData.workflowCode,
        participantCode: participantCode,
        connectionNote: formData.connectionNote,
        subscriptionLimit: formData.subscriptionLimit,
        subscriptionThreshold: formData.subscriptionThreshold,
        interestedParties: formData.interestedParties,
        enableOperations: operationsList,
        providerCustomerCodePath: providerCustomerCodePath || '',
        requireTransform: requireTransform === 'yes' ? true : false,
        authContext: serviceAuthentication === 'yes' ? serviceAuthContext : null,
        method: initData.isEdit || initData.editSettings || initData.aggregatorEdit ? 'PUT' : 'POST',
        tid: formData.tid || ''
      }
    })
      .then(response1 => {
        enqueueSnackbar(initData.isEdit ? 'Changed Successfully !!' : 'Service Enabled Successfully !!', {
          variant: 'success',
          preventDuplicate: true
        });
        setFormInfo({
          ...formInfo,
          operations: {},
          workflowCode: '',
          selectAll: ''
        });
        setDomain('');
        isServiceAuthentication('no');
        setServiceAuthContext(defaultAuthContext);
        setRequireTransform('no');
        setProviderCustomerCodePath('');
        refetch();
        subscribedParticipantRefresh();
        return response1;
      })
      .catch(error => {
        enqueueSnackbar('Service Enabled Failed', { variant: 'error', preventDuplicate: true });
        setDomain('');
        isServiceAuthentication('no');
        setServiceAuthContext(defaultAuthContext);
        setRequireTransform('no');
        setProviderCustomerCodePath('');
        setFormInfo({
          ...formInfo,
          operations: {},
          workflowCode: ''
        });
      });
  };

  useEffect(() => {
    if (initData.isEdit || initData.editSettings || initData.aggregatorEdit) {
      setFormInfo({
        operations: initData.formData.operations,
        workflowCode: initData.formData.workflowCode
      });

      if (initData?.formData?.authContext) {
        setServiceAuthContext(initData.formData.authContext);
        isServiceAuthentication('yes');
      }

      if (participantType === 'PROVIDER') {
        setProviderCustomerCodePath(initData.formData.providerCustomerCodePath);
        setRequireTransform(initData.formData.requireTransform);
      }

      const operationMap: any = {};
      // const CustOpMap: any = {};
      // const ProvOpMap: any = {};
      const serviceNameFormObj = _.filter(
        workflowList.data?.workflow?.content || [],
        (o: any) => o.workflowCode === initData.formData.workflowCode
      );
      (serviceNameFormObj?.[0]?.operations || []).forEach((operation: any) => {
        operationMap[operation.operationCode] = operation.operationName;
      });

      if (participantType === 'CUSTOMER') {
        const url = initData.formData?.operations?.[Object.keys(initData.formData.operations)[0]]?.url || '';
        const matchList = url.match(/^(https?:)\/\/(([^:/?#]*)(?:([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
        const domainName = matchList?.length > 2 ? `${matchList[1]}//${matchList[2]}` : '';
        setDomain(domainName);
        const operations: any = {};
        _.keys(initData.formData.operations || []).forEach(key => {
          const obj = {
            ...initData.formData.operations[key]
          };
          const matchPathName = obj?.url?.match(
            /^(https?:)\/\/(([^:/?#]*)(?:([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/
          );
          obj.url = matchList?.length > 2 ? matchPathName[5] : '';
          obj.operationName = operationMap[key];
          operations[key] = obj;
        });
        setFormInfo({
          operations: operations,
          workflowCode: initData.formData.workflowCode
        });
      } else {
        const operations: any = {};
        _.keys(initData.formData.operations || []).forEach(key => {
          const obj = {
            ...initData.formData.operations[key]
          };
          obj.operationName = operationMap[key];
          operations[key] = obj;
        });
        setFormInfo({
          operations: operations,
          workflowCode: initData.formData.workflowCode
        });
      }
    }

    // (serviceNameFormObj?.[0]?.operations || []).forEach((operation: any) => {
    //   if (participantType === 'CUSTOMER' && operation.initiator === 'PROVIDER') {
    //     CustOpMap[operation.operationCode] = operation.operationName;
    //   } else if (participantType === 'PROVIDER' && operation.initiator === 'CUSTOMER') {
    //     ProvOpMap[operation.operationCode] = operation.operationName;
    //   }
    // });

    // if (participantType === 'CUSTOMER') {
    //   const url = initData.formData?.operations?.[Object.keys(initData.formData.operations)[0]]?.url || '';
    //   const matchList = url.match(/^(https?:)\/\/(([^:/?#]*)(?:([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
    //   const domainName = matchList?.length > 2 ? `${matchList[1]}//${matchList[2]}` : '';
    //   setDomain(domainName);
    //   const operations: any = {};

    //   _.keys(initData.formData.operations || []).forEach(key => {
    //     const obj = {
    //       ...initData.formData.operations[key]
    //     };
    //     const matchPathName =
    //       obj?.url?.match(/^(https?:)\/\/(([^:/?#]*)(?:([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/) || '';
    //     obj.url = matchList?.length > 2 ? matchPathName[5] : '';
    //     obj.operationName = operationMap[key];
    //     operations[key] = obj;
    //   });

    //   // _.keys(CustOpMap || []).forEach(key => {
    //   //   const obj = {
    //   //     ...initData.formData.operations[key]
    //   //   };
    //   //   // _.keys(operations || []).forEach(op => {
    //   //   //   if (key !== op) {
    //   //   //     obj.operationName = CustOpMap[key];
    //   //   //     operations[key] = obj;
    //   //   //   }
    //   //   // });

    //   //   const matchPathName =
    //   //     obj?.url?.match(/^(https?:)\/\/(([^:/?#]*)(?:([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/) || '';

    //   //   obj.url = matchList?.length > 2 ? matchPathName[5] : '';
    //   // });
    //   setFormInfo({
    //     operations: operations,
    //     workflowCode: initData.formData.workflowCode
    //   });
    // } else {
    //   const operations: any = {};
    //   _.keys(initData.formData.operations || []).forEach(key => {
    //     const obj = {
    //       ...initData.formData.operations[key]
    //     };
    //     obj.operationName = operationMap[key];
    //     operations[key] = obj;
    //   });
    // _.keys(ProvOpMap || [])?.forEach(key => {
    //   console.log('ProvOpMap: '+key,initData.formData?.operations[key]);
    //   const obj = {
    //     ...initData.formData.operations[key]
    //   };
    //   _.keys(operations || []).forEach(op => {
    //     if (key !== op) {
    //       console.log('op: '+key,op,ProvOpMap[key]);
    //       obj.operationName = ProvOpMap[key];
    //       operations[key] = obj;
    //     }
    //   });
    //   console.log('okay');
    // const matchPathName = obj.url.match(
    //   /^(https?:)\/\/(([^:/?#]*)(?:([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/
    // );
    // obj.url = matchList?.length > 2 ? matchPathName[5] : '';
    // });
    //     setFormInfo({
    //       operations: operations,
    //       workflowCode: initData.formData.workflowCode
    //     });
    //   }
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantType, initData.isEdit, initData.formData, workflowList.data]);
  return (
    <div>
      <EnableServiceDialog
        dialogOpen={initData.enableService}
        formData={initData.formData}
        handleDialogClose={handleEnableServiceDialogClose}
        handleDialogSubmit={handleEnableServiceDialogSubmit}
        edit={initData.isEdit}
        allAuthentications={allAuthentications}
        participant={props.participant}
        subscribedParticipantRefresh={subscribedParticipantRefresh}
        supportUser={userRole === 'APP_ADMIN' || userRole === 'PARTICIPANT_ADMIN' ? false : true}
        handleOperationDataChange={handleOperationDataChange}
        formInfo={formInfo}
        getLatestValues={getLatestValues}
        authContextList={authContextList}
        setServiceAuthContext={setServiceAuthContext}
        isServiceAuthentication={isServiceAuthentication}
        serviceAuthentication={serviceAuthentication}
        serviceAuthContext={serviceAuthContext}
        setDomain={setDomain}
        domain={domain}
        setRequireTransform={setRequireTransform}
        requireTransform={requireTransform}
        setProviderCustomerCodePath={setProviderCustomerCodePath}
        providerCustomerCodePath={providerCustomerCodePath}
        aggregatorCode={subscribedParticipantData?.subscribedParticipantWorkflow?.content?.[0]?.aggregatorCode}
        authContext={subscribedParticipantData?.subscribedParticipantWorkflow?.content?.[0]?.authContext}
      />
      <ServiceSettingsDialog
        isOpen={initData.editSettings}
        formData={initData.formData}
        rowData={rowData}
        handleDialogClose={toggleSettingsDialog}
        formInfo={formInfo}
        handleOperationDataChange={handleOperationDataChange}
        handleDialogSubmit={handleEnableServiceDialogSubmit}
        supportUser={userRole !== 'APP_ADMIN' ? true : false}
      />
      <AggregatorSettingsDialog
        dialogOpen={initData.aggregatorEdit}
        formData={initData.formData}
        handleDialogClose={aggregatorSettingsDialog}
        handleDialogSubmit={handleEnableServiceDialogSubmit}
        edit={initData.aggregatorEdit}
        allAuthentications={allAuthentications}
        participant={props.participant}
        subscribedParticipantRefresh={subscribedParticipantRefresh}
        supportUser={userRole !== 'APP_ADMIN' ? true : false}
        handleOperationDataChange={handleOperationDataChange}
        formInfo={formInfo}
        getLatestValues={getLatestValues}
        authContextList={authContextList}
        setServiceAuthContext={setServiceAuthContext}
        isServiceAuthentication={isServiceAuthentication}
        serviceAuthentication={serviceAuthentication}
        serviceAuthContext={serviceAuthContext}
        setDomain={setDomain}
        domain={domain}
        setRequireTransform={setRequireTransform}
        requireTransform={requireTransform}
        setProviderCustomerCodePath={setProviderCustomerCodePath}
        providerCustomerCodePath={providerCustomerCodePath}
        aggregatorCode={subscribedParticipantData?.subscribedParticipantWorkflow?.content?.[0]?.aggregatorCode}
        authContext={subscribedParticipantData?.subscribedParticipantWorkflow?.content?.[0]?.authContext}
      />
      <AuditDetailsDialog
        dialogOpen={auditHistory}
        handleDialogClose={handleAuditHistoryClose}
        formData={auditData?.fetchAuditService?.content || []}
        mode={'code'}
        participantCode={participantCode}
        refresh={auditLoading}
        totalElements={auditData?.fetchAuditService?.totalElements}
      />
      {loading || subscribedParticipantLoading ? (
        <Loading />
      ) : (
        <DetailedTable
          key="participantService"
          id="participantService"
          title=""
          columns={columns[participantType]}
          data={data?.workflowSubscriberSummary || []}
          showSelection={false}
          showPagination={false}
          tableActions={participantServiceActions}
        />
      )}
    </div>
  );
};
export default ParticipantService;
